import {Modal, Button} from "react-bootstrap";
import React, {Component} from 'react';
import {fabric} from 'fabric';
import {Redirect} from "react-router-dom";
import {createPage, getPage, listPages, updatePage} from '../services/pageService';
import { jiraStory, jiraTasks, getJiraConfig } from '../services/jiraService';
import config from "../config/config";
import {transformFabricObjectsToNotes, transformFabricObjectsToTasks} from '../lib/FabricJsHelper'
import {STATES} from '../lib/PageConstants'
import {clampZoom, fitTransform} from '../lib/CanvasViewport';
import ErrorAlert from "../components/ErrorAlert";
import SuccessAlert from "../components/SuccessAlert";
import {Helmet} from "react-helmet";
import {getOrgAssignees } from "../services/orgService";
import AppBar from "../app-shell/AppBar";
import CreateDialog from "../app-shell/CreateDialog";
import editorStyles from "./Editor.module.css";
import ToolDock from "../editor/ToolDock";
import SelectedOvalToolbar from "../editor/SelectedOvalToolbar";
import SidePanel from "../editor/SidePanel";
import TasksSheet from "../editor/TasksSheet";
import Toast from "../patterns/Toast";
import MobileDetailsSheet from "../editor/MobileDetailsSheet";
import {FitScreenIcon} from "../editor/EditorIcons";

//The following prevents scaling during a circle resize (even though the strokeUniform property prevents the actual scaling happening)
fabric.Object.prototype.noScaleCache = false;

export default class Page extends Component {
    constructor(props) {
        super(props);
        this.state = {
            canvas: null,
            pageId: null,
            title: null,
            redirectUrl: null,
            creating: false,
            canvasSelectionOvalTaskPresent: false,
            canvasSelectionNotePresent: false,
            created: false,
            imagePresent: false,
            saveInProgress: false,
            saveSuccessMessage: null,
            saveErrorMessage: null,
            createPageError: null,
            clickMode: '1',
            page: null,
            persistentErrorMessage: null,
            jiraConfig: null,
            assignees: null,
            backgroundImageUpdated: false,
            backgroundFileName: null,
            backgroundFileSize: null,
            backgroundImageDataUrl: null,
            unsavedChanges: false,
            zoom: 1,
            selectedOval: null,
            hintsDismissed: {},
            sidePanelDetailsOpen: true,
            mobileTasksExpanded: false,
            mobileDetailsOpen: false,
            toast: null,
        }
        // Guards the dirty-tracking canvas listeners against firing during
        // programmatic bulk updates (initial load, post-Jira-sync refresh).
        this.suppressDirty = false;
        // The uploaded background image's own pixel size - the canvas
        // element itself is sized to fill its container instead (see
        // resizeCanvasToContainer), with pan/zoom navigating this space.
        this.imageWidth = 0;
        this.imageHeight = 0;
        this.canvasAreaRef = React.createRef();
        this.isSpaceDown = false;
        this.isPanning = false;
        this.panLast = null;
        this.pinch = null;
    }

    markUnsaved = () => {
        if (this.suppressDirty) return;
        this.setState({unsavedChanges: true});
    }

    // Sizes the fabric canvas element to fill its container (rather than the
    // uploaded image's own pixel size), so pan/zoom has room to navigate in.
    resizeCanvasToContainer = () => {
        const canvas = this.state.canvas;
        const area = this.canvasAreaRef.current;
        if (!canvas || !area) return;
        canvas.setDimensions({width: area.clientWidth, height: area.clientHeight});
        canvas.renderAll();
    }

    // Sets the background image at its natural size and sizes the canvas
    // element to the container, then fits the image to the visible area.
    applyBackgroundImage = (fabricImage, canvas) => {
        this.imageWidth = fabricImage.width;
        this.imageHeight = fabricImage.height;
        fabricImage.set({left: 0, top: 0, originX: 'left', originY: 'top', selectable: false, evented: false});
        const area = this.canvasAreaRef.current;
        if (area) {
            canvas.setDimensions({width: area.clientWidth, height: area.clientHeight});
        }
        canvas.setBackgroundImage(fabricImage, canvas.renderAll.bind(canvas));
        this.fitToScreen();
    }

    fitToScreen = () => {
        const canvas = this.state.canvas;
        const area = this.canvasAreaRef.current;
        if (!canvas || !area || !this.imageWidth || !this.imageHeight) return;
        const {zoom, panX, panY} = fitTransform(this.imageWidth, this.imageHeight, area.clientWidth, area.clientHeight);
        canvas.setViewportTransform([zoom, 0, 0, zoom, panX, panY]);
        canvas.renderAll();
        this.setState({zoom});
        this.updateSelectedOvalPosition();
    }

    setZoom = (zoom, point) => {
        const canvas = this.state.canvas;
        if (!canvas) return;
        const clamped = clampZoom(zoom);
        canvas.zoomToPoint(point || new fabric.Point(canvas.getWidth() / 2, canvas.getHeight() / 2), clamped);
        canvas.renderAll();
        this.setState({zoom: clamped});
        this.updateSelectedOvalPosition();
    }

    async componentDidMount() {
        const {pageId} = this.props.match.params;

        this.setState({pageId: pageId})

        if (this.props.authentication?.organisation) {
            try {
                const assigneeResponse = await getOrgAssignees(this.props.authentication.organisation.id);
                this.setState({
                    assignees: assigneeResponse.assignees,
                });
            } catch (error) {
                this.setState({
                    persistentErrorMessage: `Problem getting assignees: ${error.toString()}`
                });
            }
        }

        const canvas = new fabric.Canvas('c');
        if (pageId === 'new') {
            try {
                if (this.props.authentication) {
                    const stashedPage = sessionStorage.getItem('stashedPage');
                    if (stashedPage) {
                        //If there is a stashed page then always clear it immediately
                        sessionStorage.removeItem('stashedPage')
                    }
                    //User is logged in - get their pages and redirect to banding if limit is reached
                    const pages = await listPages();
                    if (pages.length >= this.props.authentication.features.pageLimit) {
                        console.warn(`User has ${pages.length} pages, redirecting to banding page`);
                        this.setState({redirectUrl: '/banding'})
                    } else {
                        if (stashedPage) {
                            //attempt to save stashed page
                            try {
                                const newPageId = await createPage(stashedPage, null);
                                console.log(`post response was: ${newPageId}`)
                                //redirect to new page on success
                                this.setState({redirectUrl: `/pages/${newPageId}`})
                                return;
                            } catch (err) {
                                //display error and load stashed page on screen - user can decide what to do next
                                await this.loadStashedPage(stashedPage, canvas);
                                this.setState({
                                    saveErrorMessage: `Error saving your page after logging in: ${err}`,
                                    creating: true,
                                    created: true
                                })
                            }
                        } else {
                            // User is logged in, has no stashed page, and is within their page limit
                            // proceed to create page dialog
                            this.setState({canvas, creating: true});
                        }
                    }
                } else {
                    //User is not logged in, proceed to create page dialog
                    this.setState({canvas, creating: true});
                }
            } catch (error) {
                this.setState({
                    persistentErrorMessage: `Problem getting page count: ${error.toString()}`
                });
            }
        } else {
            if (!this.props.authentication) {
                console.log('attempting to view page when not logged in. Redirecting to home');
                this.setState({redirectUrl: '/'})
                return;
            }
            await this.loadExistingPage(pageId, canvas, this.state.assignees);

            //Get jira config if org is present
            if (this.props.authentication.organisation) {
                try {
                    const jiraConfigResponse = await getJiraConfig(this.props.authentication.organisation.id)

                    if (jiraConfigResponse.config.length > 0) {
                        this.setState ({jiraConfig:  jiraConfigResponse.config[0]})
                    }
                } catch (error) {
                    this.setState({
                        persistentErrorMessage: `Problem getting Jira Org: ${error.toString()}`
                    });
                }

            }
        }

        canvas.on('mouse:down', this.handleCanvasMouseDown)
        canvas.on('mouse:move', this.handleCanvasMouseMove)
        canvas.on('mouse:up', this.handleCanvasMouseUp)
        canvas.on('mouse:wheel', this.handleWheel)
        canvas.on('object:moving', this.updateSelectedOvalPosition)
        canvas.on('object:scaling', this.updateSelectedOvalPosition)
        canvas.on('selection:cleared', this.canvasSelection)
        canvas.on('selection:updated', this.canvasSelection)
        canvas.on('selection:created', this.canvasSelection)
        // UI-only dirty tracking for the app bar's "Unsaved changes" pill.
        // Attached after the initial load above, so populating the canvas
        // from saved data doesn't itself mark the page unsaved.
        canvas.on('object:added', this.markUnsaved)
        canvas.on('object:removed', this.markUnsaved)
        canvas.on('object:modified', this.markUnsaved)
        this.fileSelector = this.buildFileSelector();

        // fabric.js normalises single-touch to its mouse:* events, but not
        // pinch (multi-touch) - handled directly on the native element.
        const upperCanvasEl = canvas.upperCanvasEl;
        upperCanvasEl.addEventListener('touchstart', this.handleTouchStart, {passive: false});
        upperCanvasEl.addEventListener('touchmove', this.handleTouchMove, {passive: false});
        upperCanvasEl.addEventListener('touchend', this.handleTouchEnd);

        window.addEventListener('resize', this.handleWindowResize);
        document.addEventListener('keydown', this.handleKeyDown);
        document.addEventListener('keyup', this.handleKeyUp);
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.handleWindowResize);
        document.removeEventListener('keydown', this.handleKeyDown);
        document.removeEventListener('keyup', this.handleKeyUp);
        const upperCanvasEl = this.state.canvas?.upperCanvasEl;
        if (upperCanvasEl) {
            upperCanvasEl.removeEventListener('touchstart', this.handleTouchStart);
            upperCanvasEl.removeEventListener('touchmove', this.handleTouchMove);
            upperCanvasEl.removeEventListener('touchend', this.handleTouchEnd);
        }
    }

    loadExistingPage = async (pageId, canvas, assignees) => {
        try {
            const data = await getPage(pageId);

            var img = new Image();

            let self = this;

            img.onload = function () {
                //todo consider scaling image to a max height/width here
                var f_img = new fabric.Image(img);

                self.applyBackgroundImage(f_img, canvas);

                //loop through each oval and create OvalTask obj & add to canvas
                data.tasks.forEach(task => {
                    const ovalTask = new fabric.OvalTask(
                        task.canvasProperties,
                        {fontSize: task.textSize},
                        task.text,
                        STATES[task.status],
                        assignees?.find(user => user.id === task.assigneeId) || null,
                        task.id,
                        task.jiraKey)
                    canvas.add(ovalTask)
                })

                //now notes
                data.notes.forEach(note => {
                    const ovNote = new fabric.OvNote(note.text, {
                        top: note.top,
                        left: note.left,
                        width: note.width,
                        height: note.height,
                        angle: note.angle,
                        fontSize: note.textSize})
                    canvas.add(ovNote)
                })

                canvas.renderAll();

                self.setState({
                    title: data.title,
                    canvas: canvas,
                    page: data,
                    backgroundImageDataUrl: data.backgroundImage,
                })
            };

            img.src = data.backgroundImage;

        } catch (error) {
            this.setState({
                persistentErrorMessage: `Problem getting page: ${error.toString()}`
            });
        }
    }

    loadStashedPage = async (stashedPage, canvas) => {
        try {
            const data = JSON.parse(stashedPage)
            var img = new Image();

            let self = this;

            img.onload = function () {
                //todo consider scaling image to a max height/width here
                var f_img = new fabric.Image(img);

                self.applyBackgroundImage(f_img, canvas);

                //loop through each oval and create OvalTask obj & add to canvas
                data.tasks.forEach(task => {
                    const ovalTask = new fabric.OvalTask(
                        task.canvasProperties,
                        {fontSize: task.textSize},
                        task.text,
                        STATES[task.status],
                        this.state.assignees?.find(user => user.id === task.assigneeId) || null,
                        task.id,
                        task.jiraKey)
                    canvas.add(ovalTask)
                })

                //now notes
                data.notes.forEach(note => {
                    const ovNote = new fabric.OvNote(note.text, {
                        top: note.top,
                        left: note.left,
                        width: note.width,
                        height: note.height,
                        angle: note.angle,
                        fontSize: note.textSize})
                    canvas.add(ovNote)
                })

                canvas.renderAll();

                self.setState({
                    title: data.title,
                    canvas: canvas,
                    page: data,
                    backgroundImageDataUrl: data.backgroundImage,
                })
            };

            img.src = data.backgroundImage;

        } catch (error) {
            this.setState({
                persistentErrorMessage: `Problem getting page: ${error.toString()}`
            });
        }
    }

    processFile = (event) => {
        const file = event.target.files?.[0];
        if (!file) {
            // Picker was cancelled, or a non-file drag landed on the dropzone.
            return;
        }

        const canvas = this.state.canvas
        var img = new Image();

        let self = this;

        img.onload = function () {
            //todo consider scaling image to a max height/width here
            var f_img = new fabric.Image(img);

            self.applyBackgroundImage(f_img, canvas);

            self.setState({
                imagePresent: true,
                createPageError: null,
                backgroundFileName: file.name,
                backgroundFileSize: file.size,
                unsavedChanges: true,
            })
        };

        let reader = new FileReader();
        reader.onload = function () {
            img.src = reader.result;
            self.setState({backgroundImageDataUrl: reader.result})
        };
        reader.onerror = function (error) {
            console.error('Error: ', error);
        };

        const fileSize = file.size
        if (fileSize > config.page.max_image_size_bytes) {
            const msg = `file must be < ${config.page.max_image_size_bytes} bytes. Was: ${fileSize}`;
            if (this.state.creating && !this.state.created) {
                this.setState({createPageError: msg})
            } else {
                this.setState({saveErrorMessage: msg})
            }
            console.error(msg)
        } else {
            reader.readAsDataURL(file);
            this.setState({backgroundImageUpdated: true})
        }
    }

    removeBackground = () => {
        this.state.canvas.setBackgroundImage(null);
        this.state.canvas.renderAll();
        this.setState({
            imagePresent: false,
            backgroundFileName: null,
            backgroundFileSize: null,
            backgroundImageDataUrl: null,
        })
    }

    deleteObject = () => {
        const object = this.state.canvas.getActiveObject();

        if (!object) {
            return;
        }

        if (object.type === 'activeSelection') {
            object.getObjects().forEach(obj => {
                this.state.canvas.remove(obj)
            })
        } else {
            if (!object.isEditing) {
                this.state.canvas.remove(object);
            }
        }
        this.state.canvas.renderAll()
        this.updateSelectedOvalPosition()
    }

    enableJiraIntegration = async () => {
        this.setState({saveInProgress: true})
        //do save
        const saveSuccess = await this.save(false, false);
        if (!saveSuccess) {
            return;
        }
        console.log(`Completed pre jira integration save`);

        let story;
        try {
            const res = await jiraStory(this.state.pageId, this.props.authentication.organisation.id)
            story = res.key
        } catch (e) {
            this.setState({saveErrorMessage: `Error enabling Jira integration: ${e}`, saveInProgress: false});
            return;
        }
        console.log(`Called story endpoint`);

        //todo probably doesn't need a full sync here
        //refresh page
        try {
        await this.refreshPage();
        } catch (e) {
            this.setState({saveErrorMessage: `Error refreshing page following Jira integration: ${e}`, saveInProgress: false});
            return;
        }
        console.log(`refreshed page`);
        this.setState({saveSuccessMessage: `Jira integration is now enabled. Story: ${story}`, saveInProgress: false})
    }

    performJiraSync = async () => {
        this.setState({saveInProgress: true})

        //do save
        const saveSuccess = await this.save(false, false);
        if (!saveSuccess) {
            return;
        }
        console.log(`Completed pre jira sync save`);

        try {
            await jiraTasks(this.state.pageId, this.props.authentication.organisation.id)
        } catch (e) {
            this.setState({saveErrorMessage: `Error performing jira sync: ${e}`, saveInProgress: false});
            return;
        }
        console.log(`Called tasks endpoint`);

        //refresh page
        try {
            await this.refreshPage();
        } catch (e) {
            this.setState({saveErrorMessage: `Error refreshing page following push to Jira: ${e}`, saveInProgress: false});
            return;
        }
        console.log(`refreshed page`);
        this.setState({saveSuccessMessage: `All tasks pushed to Jira`, saveInProgress: false})
    }

    download = async () => {
        const image = this.state.canvas.toDataURL({
            format: 'png',
            quality: 1
        });

        const a = document.createElement("a");
        a.href = image;
        a.download = "image.png";
        a.click();
    }

    /**
     *  updateInProgressStateVar - update saveInProgress state var. Defaults to true. Should be false if the caller already does thiis
     *  displaySuccessMessage - show success message. true by default
     */
    save = async (updateInProgressStateVar = true, displaySuccessMessage = true) => {
        if (updateInProgressStateVar) {
            this.setState({saveInProgress: true})
        }
        const json = this.state.canvas.toJSON()

        try {
            if (this.state.creating) {
                if (!this.props.authentication) {
                    console.log('not logged in, prompt for log in');
                    this.setState({showSignInModal: true})
                } else {
                    console.log('new page, will POST');
                    const exportedImage = this.state.canvas.toDataURL({
                        format: 'png',
                        quality: 1
                    });
                    const newPageId = await createPage(JSON.stringify({
                        title: this.state.title,
                        backgroundImage: json.backgroundImage.src,
                        tasks: transformFabricObjectsToTasks(json.objects),
                        notes: transformFabricObjectsToNotes((json.objects))
                    }), exportedImage);
                    console.log(`post response was: ${newPageId}`)
                    this.setState({redirectUrl: `/pages/${newPageId}`, unsavedChanges: false})
                }
            } else {
                console.log('existing page, will PUT');
                const exportedImage = this.state.canvas.toDataURL({
                    format: 'png',
                    quality: 1
                });
                await updatePage(this.state.pageId, JSON.stringify({
                    title: this.state.title,
                    backgroundImage: this.state.backgroundImageUpdated ? json.backgroundImage.src : null,
                    tasks: transformFabricObjectsToTasks(json.objects),
                    notes: transformFabricObjectsToNotes((json.objects))
                }), exportedImage )

                await this.refreshPage();
                this.setState({unsavedChanges: false})

                if (displaySuccessMessage) {
                    this.setState({saveSuccessMessage: `Successfully saved page '${this.state.title}'`})
                    this.setState({backgroundImageUpdated: false})
                }
            }
        } catch (e) {
            if (e.response?.data?.error) {
                this.setState({saveErrorMessage: `Error saving page: ${e.response.data.error}`});
            } else {
                //Not a recognised error, just show what we can
                this.setState({saveErrorMessage: `Error saving page:  ${e.toString()}`});
            }
            this.setState({
                saveInProgress: false,
            })
            return false;
        }
        if (updateInProgressStateVar) {
            this.setState({saveInProgress: false})
        }
        return true;
    }

    refreshPage = async() => {
        const page = await getPage(this.state.pageId)
        this.setState({modifiedAt: page.modifiedAt, page});

        // Programmatic rebuild, not a user edit - don't flag unsaved changes.
        this.suppressDirty = true;

        //Clear oval tasks & recreate to take populate new Jira tasks
        this.state.canvas.getObjects().filter((obj) => obj.type === "ovalTask").forEach(task => {
            this.state.canvas.remove(task)
        })

        page.tasks.forEach(task => {
            const ovalTask = new fabric.OvalTask(
                task.canvasProperties,
                {fontSize: task.textSize},
                task.text,
                STATES[task.status],
                this.state.assignees?.find(user => user.id === task.assigneeId) || null,
                task.id,
                task.jiraKey)
            this.state.canvas.add(ovalTask)
        });
        this.state.canvas.renderAll();
        this.suppressDirty = false;
    }

    create = async () => {
        this.setState({created: true})
    }

    cancelCreate = async () => {
        this.props.history.goBack();
    }

    signInClicked = async () => {
        console.log('stashing page')
        const json = this.state.canvas.toJSON();
        sessionStorage.setItem('stashedPage', JSON.stringify({
            title: this.state.title,
            backgroundImage: json.backgroundImage.src,
            tasks: transformFabricObjectsToTasks(json.objects),
            notes: transformFabricObjectsToNotes(json.objects)
        }));
        this.setState({redirectUrl: '/login'})
    }

    cancelSignIn = async () => {
        this.setState({showSignInModal: false})
    }

    isTypingTarget = (target) => !!target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)

    setClickMode = (mode) => {
        const canvas = this.state.canvas;
        if (canvas) {
            canvas.discardActiveObject();
            canvas.selection = mode === '1';
            canvas.defaultCursor = mode === '1' ? 'default' : 'crosshair';
            canvas.renderAll();
        }
        this.setState({clickMode: mode, selectedOval: null});
    }

    dismissHint = (mode) => {
        if (this.state.hintsDismissed[mode]) return;
        this.setState({hintsDismissed: {...this.state.hintsDismissed, [mode]: true}});
    }

    checkOvalLimit = () => {
        const taskLimit = this.props.authentication?.organisation?.config?.taskLimit ? this.props.authentication.organisation.config.taskLimit : config.page.default_max_circles
        if (this.state.canvas.getObjects().filter((obj) => obj.type === "ovalTask").length >= taskLimit) {
            this.setState({saveErrorMessage: `${taskLimit} oval limit in your Ovalview plan`});
            return false;
        }
        return true;
    }

    placeNoteAt = (pointer) => {
        const canvas = this.state.canvas;
        const noteLimit = this.props.authentication?.organisation?.config?.noteLimit ? this.props.authentication.organisation.config.noteLimit : config.page.default_max_circles
        if (canvas.getObjects().filter((obj) => obj.type === "ovNote").length >= noteLimit) {
            this.setState({saveErrorMessage: `${noteLimit} note limit in your Ovalview plan`});
            return;
        }
        const note = new fabric.OvNote('', {left: pointer.x, top: pointer.y})
        canvas.add(note)
        canvas.renderAll();
        this.dismissHint('3');
    }

    // Pan (space+drag on desktop, one-finger drag in Select mode on touch)
    // and drag-to-draw ovals share the mouse:down/move/up trio below; plain
    // clicks/drags otherwise fall through to fabric's own selection/move.
    handleCanvasMouseDown = (opt) => {
        const canvas = this.state.canvas;
        const evt = opt.e;
        const isTouch = !!evt.type && evt.type.startsWith('touch');
        const touchPoint = evt.touches?.[0];

        if (this.isSpaceDown || (isTouch && this.state.clickMode === '1' && !opt.target && !evt.touches?.[1])) {
            this.isPanning = true;
            canvas.selection = false;
            canvas.discardActiveObject();
            canvas.defaultCursor = 'grabbing';
            this.panLast = {x: touchPoint ? touchPoint.clientX : evt.clientX, y: touchPoint ? touchPoint.clientY : evt.clientY};
            return;
        }

        if (this.state.clickMode === '2' && !opt.target && this.checkOvalLimit()) {
            const pointer = canvas.getPointer(evt);
            this.drawStart = pointer;
            this.drawingOval = new fabric.OvalTask({left: pointer.x, top: pointer.y, rx: 1, ry: 1}, {}, '', STATES.NOT_DONE, null);
            canvas.add(this.drawingOval);
            canvas.renderAll();
        }
    }

    handleCanvasMouseMove = (opt) => {
        const canvas = this.state.canvas;
        const evt = opt.e;

        if (this.isPanning && this.panLast) {
            const touchPoint = evt.touches?.[0];
            const clientX = touchPoint ? touchPoint.clientX : evt.clientX;
            const clientY = touchPoint ? touchPoint.clientY : evt.clientY;
            canvas.relativePan({x: clientX - this.panLast.x, y: clientY - this.panLast.y});
            this.panLast = {x: clientX, y: clientY};
            this.updateSelectedOvalPosition();
            return;
        }

        if (this.drawingOval) {
            const pointer = canvas.getPointer(evt);
            const rx = Math.max(20, Math.abs(pointer.x - this.drawStart.x) / 2);
            const ry = Math.max(14, Math.abs(pointer.y - this.drawStart.y) / 2);
            this.drawingOval.set({
                left: (pointer.x + this.drawStart.x) / 2,
                top: (pointer.y + this.drawStart.y) / 2,
                rx, ry,
            });
            this.drawingOval.recalcTextPosition();
            canvas.renderAll();
        }
    }

    handleCanvasMouseUp = (opt) => {
        const canvas = this.state.canvas;

        if (this.isPanning) {
            this.isPanning = false;
            this.panLast = null;
            canvas.selection = this.state.clickMode === '1';
            canvas.defaultCursor = this.isSpaceDown ? 'grab' : 'default';
            return;
        }

        if (this.drawingOval) {
            const oval = this.drawingOval;
            this.drawingOval = null;
            this.drawStart = null;
            canvas.setActiveObject(oval);
            canvas.renderAll();
            this.dismissHint('2');
            this.markUnsaved();
            return;
        }

        if (this.state.clickMode === '3' && !opt.target) {
            this.placeNoteAt(canvas.getPointer(opt.e));
        }
    }

    handleWheel = (opt) => {
        const evt = opt.e;
        evt.preventDefault();
        evt.stopPropagation();
        const canvas = this.state.canvas;
        if (evt.ctrlKey || evt.metaKey) {
            const zoom = clampZoom(canvas.getZoom() * (0.999 ** evt.deltaY));
            canvas.zoomToPoint({x: evt.offsetX, y: evt.offsetY}, zoom);
            this.setState({zoom});
        } else {
            canvas.relativePan({x: -evt.deltaX, y: -evt.deltaY});
        }
        this.updateSelectedOvalPosition();
    }

    handleTouchStart = (e) => {
        if (e.touches.length === 2) {
            e.preventDefault();
            const [t1, t2] = e.touches;
            this.pinch = {
                distance: Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY),
                zoom: this.state.canvas.getZoom(),
            };
        }
    }

    handleTouchMove = (e) => {
        if (e.touches.length === 2 && this.pinch) {
            e.preventDefault();
            const canvas = this.state.canvas;
            const [t1, t2] = e.touches;
            const distance = Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);
            const zoom = clampZoom(this.pinch.zoom * (distance / this.pinch.distance));
            const rect = canvas.upperCanvasEl.getBoundingClientRect();
            canvas.zoomToPoint({
                x: (t1.clientX + t2.clientX) / 2 - rect.left,
                y: (t1.clientY + t2.clientY) / 2 - rect.top,
            }, zoom);
            this.setState({zoom});
            this.updateSelectedOvalPosition();
        }
    }

    handleTouchEnd = (e) => {
        if (e.touches.length < 2) this.pinch = null;
    }

    handleWindowResize = () => {
        this.resizeCanvasToContainer();
        this.updateSelectedOvalPosition();
    }

    handleKeyDown = (e) => {
        if (this.isTypingTarget(e.target)) return;
        if (e.code === 'Delete' || e.code === 'Backspace') {
            this.deleteObject();
        } else if (e.code === 'Space') {
            e.preventDefault();
            this.isSpaceDown = true;
            const canvas = this.state.canvas;
            if (canvas) canvas.defaultCursor = 'grab';
        } else if (e.code === 'Escape') {
            this.setClickMode('1');
        } else if (e.key === 'v' || e.key === 'V') {
            this.setClickMode('1');
        } else if (e.key === 'o' || e.key === 'O') {
            this.setClickMode('2');
        } else if (e.key === 'n' || e.key === 'N') {
            this.setClickMode('3');
        }
    }

    handleKeyUp = (e) => {
        if (e.code === 'Space') {
            this.isSpaceDown = false;
            const canvas = this.state.canvas;
            if (canvas) canvas.defaultCursor = 'default';
        }
    }

    // Recomputes the floating selected-oval toolbar's screen position (or
    // clears it when nothing/non-oval is selected). Desktop: centred above
    // the oval, flipping below if there's no room. Mobile: pinned full-width
    // above the tool dock, per the design, regardless of the oval's position.
    updateSelectedOvalPosition = () => {
        const canvas = this.state.canvas;
        const area = this.canvasAreaRef.current;
        if (!canvas || !area) return;
        const obj = canvas.getActiveObject();

        if (!obj || obj.type !== 'ovalTask') {
            if (this.state.selectedOval) this.setState({selectedOval: null});
            return;
        }

        const assignee = this.state.assignees?.find((a) => a.id === obj.assignee) || null;
        const base = {taskId: obj.taskId, text: obj.text.text, status: obj.state, assignee};

        if (area.clientWidth < 768) {
            this.setState({selectedOval: {...base, style: {left: 16, right: 16, bottom: 372, top: 'auto'}}});
            return;
        }

        const rect = obj.getBoundingRect(true, true);
        const toolbarWidth = 280;
        const toolbarHeight = 60;
        let left = rect.left + rect.width / 2 - toolbarWidth / 2;
        left = Math.max(8, Math.min(left, area.clientWidth - toolbarWidth - 8));
        let top = rect.top - toolbarHeight;
        if (top < 8) {
            top = rect.top + rect.height + 8;
        }
        this.setState({selectedOval: {...base, style: {left, top}}});
    }

    handleOvalStatusChange = (key) => {
        const canvas = this.state.canvas;
        const obj = canvas?.getActiveObject();
        if (!obj || obj.type !== 'ovalTask') return;
        obj.setState(STATES[key]);
        obj.dirty = true;
        obj.text.dirty = true;
        canvas.renderAll();
        this.markUnsaved();
        this.updateSelectedOvalPosition();
    }

    handleOvalAssigneeChange = (assignee) => {
        const canvas = this.state.canvas;
        const obj = canvas?.getActiveObject();
        if (!obj || obj.type !== 'ovalTask') return;
        obj.setAvatar(assignee);
        canvas.renderAll();
        this.markUnsaved();
        this.updateSelectedOvalPosition();
    }

    deleteSelectedOvalWithUndo = () => {
        const canvas = this.state.canvas;
        const obj = canvas?.getActiveObject();
        if (!obj || obj.type !== 'ovalTask') return;

        canvas.remove(obj);
        canvas.discardActiveObject();
        canvas.renderAll();
        this.markUnsaved();

        this.setState({
            selectedOval: null,
            toast: {
                message: 'Oval deleted',
                actionLabel: 'Undo',
                onAction: () => {
                    canvas.add(obj);
                    canvas.setActiveObject(obj);
                    canvas.renderAll();
                    this.setState({toast: null});
                    this.updateSelectedOvalPosition();
                },
            },
        });
    }

    handleTaskListSelect = (taskId) => {
        const canvas = this.state.canvas;
        const area = this.canvasAreaRef.current;
        const obj = canvas?.getObjects().find((o) => o.type === 'ovalTask' && o.taskId === taskId);
        if (!obj) return;

        canvas.setActiveObject(obj);
        const rect = obj.getBoundingRect(true, true);
        const margin = 60;
        if (area && (rect.left < margin || rect.top < margin
            || rect.left + rect.width > area.clientWidth - margin
            || rect.top + rect.height > area.clientHeight - margin)) {
            const zoom = canvas.getZoom();
            const vpt = canvas.viewportTransform.slice();
            vpt[4] = area.clientWidth / 2 - obj.left * zoom;
            vpt[5] = area.clientHeight / 2 - obj.top * zoom;
            canvas.setViewportTransform(vpt);
        }
        canvas.renderAll();
        this.setState({mobileTasksExpanded: false});
        this.updateSelectedOvalPosition();
    }

    getTasksList = () => {
        const canvas = this.state.canvas;
        if (!canvas) return [];
        return canvas.getObjects()
            .filter((o) => o.type === 'ovalTask')
            .sort((a, b) => (a.top - b.top) || (a.left - b.left))
            .map((o) => ({
                id: o.taskId,
                text: o.text.text,
                status: o.state,
                assignee: this.state.assignees?.find((a) => a.id === o.assignee) || null,
            }));
    }

    canvasSelection = () => {
        this.updateSelectedOvalPosition();
        if (this.selectedObjectIsOfType('ovalTask')) {
            this.setState({
                canvasSelectionOvalTaskPresent: true,
                canvasSelectionNotePresent: false
            })
        } else if (this.selectedObjectIsOfType('ovNote')) {
            this.setState({
                canvasSelectionNotePresent: true,
                canvasSelectionOvalTaskPresent: false,
            })
        } else {
            this.setState({
                canvasSelectionOvalTaskPresent: false,
                canvasSelectionNotePresent: false
            })
        }
    }

    selectedObjectIsOfType(type) {
        return this.state.canvas.getActiveObject()
            && (this.state.canvas.getActiveObject().type === type ||
                (this.state.canvas.getActiveObject().type === 'activeSelection'
                    && this.state.canvas.getActiveObject().getObjects().filter(obj => obj.type === type).length === this.state.canvas.getActiveObject().getObjects().length));
    }

    titleChanged = (event) => {
        this.setState({title: event.target.value, unsavedChanges: true});
    }

    buildFileSelector = () => {
        const fileSelector = document.createElement('input');
        fileSelector.setAttribute('type', 'file');
        fileSelector.setAttribute('accept', 'image/*');
        fileSelector.onchange = this.processFile;
        return fileSelector;
    }

    handleFileSelect = (e) => {
        if (e) e.preventDefault();
        this.fileSelector.click();
    }

    copyLiveLink = (e) => {
        navigator.clipboard.writeText(`${config.liveLink.baseUrl}/${this.state.page.id}.png`);
        this.setState({saveSuccessMessage: `Copied Live Link URL to clipboard`, saveInProgress: false})
    }

    clearSaveSuccessMessage = () => {
        this.setState({
            saveSuccessMessage: null,
        })
    }

    clearSaveErrorMessage = () => {
        this.setState({
            saveErrorMessage: null,
        })
    }



    render() {
        const nameFilled = !!(this.state.title && this.state.title.trim().length > 0);
        const readyToCreate = this.state.imagePresent && nameFilled;
        const dialogOpen = this.state.creating && !this.state.created;
        const unsaved = this.state.creating || this.state.unsavedChanges;
        // Matches the old sidebar's `this.state.canvas && ...` guard: don't show
        // the app bar's Save/Download/etc, or the canvas/panel, until the
        // canvas (and so either the create dialog or a loaded page) exists.
        const appBarMinimal = !this.state.canvas || dialogOpen;
        const editorReady = !!this.state.canvas && ((!this.state.creating) || (this.state.creating && this.state.created));
        const tasksList = editorReady ? this.getTasksList() : [];
        const selectedTaskId = this.state.selectedOval?.taskId || null;
        const jira = editorReady && this.state.jiraConfig ? {
            hasStory: !!this.state.page?.jiraKey,
            storyKey: this.state.page?.jiraKey,
            storyUrl: this.state.page?.jiraKey ? `${this.state.jiraConfig.host}/browse/${this.state.page.jiraKey}` : null,
            onEnable: this.enableJiraIntegration,
            onSync: this.state.page?.jiraKey ? this.performJiraSync : null,
            busy: this.state.saveInProgress,
        } : null;

        if (this.state.redirectUrl) {
            return <Redirect to={this.state.redirectUrl}/>
        } else {
            return (
                <div style={{width: '100%', minHeight: '100dvh', display: 'flex', flexDirection: 'column'}}>
                    <Helmet>
                        <title>Ovalview | {this.state.title || 'Page'}</title>
                    </Helmet>

                    {/* Inert behind the create dialog's scrim while it's open, matching
                        the reference's own aria-hidden backdrop. */}
                    <div aria-hidden={dialogOpen} style={{display: 'flex', flexDirection: 'column', flex: '1 0 auto', minHeight: 0}}>
                    <AppBar
                        minimal={appBarMinimal}
                        pageName={this.state.title || 'Untitled page'}
                        unsaved={unsaved}
                        saveInProgress={this.state.saveInProgress}
                        onSave={() => this.save()}
                        onDownload={this.download}
                        onPageDetails={() => this.setState({mobileDetailsOpen: true})}
                        authentication={this.props.authentication}
                    />

                    <ErrorAlert message={this.state.persistentErrorMessage} delay={0}/>
                    <ErrorAlert message={this.state.saveErrorMessage} onClose={this.clearSaveErrorMessage} delay={3000}/>
                    <SuccessAlert message={this.state.saveSuccessMessage} onClose={this.clearSaveSuccessMessage}/>

                    <div className={editorStyles.body}>
                        <main aria-label="Page canvas" className={editorStyles.canvasArea} ref={this.canvasAreaRef}>
                            <div style={{position: 'absolute', inset: 0, visibility: editorReady ? 'visible' : 'hidden'}}>
                                <canvas id="c"/>
                            </div>

                            {!editorReady && !this.state.persistentErrorMessage && (
                                <div className={editorStyles.canvasAreaLoading}>Loading page…</div>
                            )}

                            {editorReady && (
                                <>
                                    <button type="button" aria-label="Fit page to screen"
                                            className={editorStyles.fitButton} onClick={this.fitToScreen}>
                                        <FitScreenIcon/>
                                    </button>

                                    {this.state.selectedOval && (
                                        <SelectedOvalToolbar
                                            label={this.state.selectedOval.text}
                                            style={this.state.selectedOval.style}
                                            status={this.state.selectedOval.status}
                                            onStatusChange={this.handleOvalStatusChange}
                                            assignee={this.state.selectedOval.assignee}
                                            assignees={this.state.assignees}
                                            onAssigneeChange={this.handleOvalAssigneeChange}
                                            onDelete={this.deleteSelectedOvalWithUndo}
                                        />
                                    )}

                                    <ToolDock
                                        mode={this.state.clickMode}
                                        onModeChange={this.setClickMode}
                                        hintDismissed={!!this.state.hintsDismissed[this.state.clickMode]}
                                        onDismissHint={() => this.dismissHint(this.state.clickMode)}
                                    />

                                    <TasksSheet
                                        tasks={tasksList}
                                        selectedTaskId={selectedTaskId}
                                        onSelectTask={this.handleTaskListSelect}
                                        expanded={this.state.mobileTasksExpanded}
                                        onToggleExpand={() => this.setState((s) => ({mobileTasksExpanded: !s.mobileTasksExpanded}))}
                                        onOpenPageDetails={() => this.setState({mobileDetailsOpen: true})}
                                    />
                                </>
                            )}
                        </main>

                        {editorReady && (
                            <SidePanel
                                title={this.state.title}
                                onTitleChange={this.titleChanged}
                                backgroundThumbnail={this.state.backgroundImageDataUrl}
                                onReplaceBackground={this.handleFileSelect}
                                detailsOpen={this.state.sidePanelDetailsOpen}
                                onToggleDetails={() => this.setState((s) => ({sidePanelDetailsOpen: !s.sidePanelDetailsOpen}))}
                                tasks={tasksList}
                                selectedTaskId={selectedTaskId}
                                onSelectTask={this.handleTaskListSelect}
                                jira={jira}
                                liveLink={this.state.page ? {
                                    url: `${config.liveLink.baseUrl}/${this.state.page.id}.png`,
                                    onCopy: this.copyLiveLink,
                                } : null}
                            />
                        )}
                    </div>
                    </div>

                    <CreateDialog
                        open={dialogOpen}
                        imagePresent={this.state.imagePresent}
                        backgroundFileName={this.state.backgroundFileName}
                        backgroundFileSize={this.state.backgroundFileSize}
                        backgroundImageDataUrl={this.state.backgroundImageDataUrl}
                        title={this.state.title}
                        error={this.state.createPageError}
                        ready={readyToCreate}
                        onTitleChange={this.titleChanged}
                        onFileSelected={this.processFile}
                        onRemoveBackground={this.removeBackground}
                        onCancel={this.cancelCreate}
                        onSubmit={this.create}
                    />

                    <MobileDetailsSheet
                        open={this.state.mobileDetailsOpen}
                        title={this.state.title}
                        onTitleChange={this.titleChanged}
                        backgroundThumbnail={this.state.backgroundImageDataUrl}
                        onReplaceBackground={() => {
                            this.setState({mobileDetailsOpen: false});
                            this.handleFileSelect();
                        }}
                        onClose={() => this.setState({mobileDetailsOpen: false})}
                    />

                    {this.state.toast && (
                        <Toast
                            message={this.state.toast.message}
                            actionLabel={this.state.toast.actionLabel}
                            onAction={this.state.toast.onAction}
                            onDismiss={() => this.setState({toast: null})}
                        />
                    )}

                    <Modal show={this.state.showSignInModal} onHide={this.cancelSignIn} aria-label={'signInModal'}>
                        <Modal.Header closeButton>
                            <Modal.Title>Sign in required</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <p>You must first Sign in or Sign up before you can save a diagram</p>
                        </Modal.Body>
                        <Modal.Footer>

                            <Button variant="secondary" onClick={this.cancelSignIn}>Cancel</Button>
                            <Button variant="primary" onClick={this.signInClicked}>
                                Sign in / Sign Up
                            </Button>
                        </Modal.Footer>
                    </Modal>


                </div>
            );
        }
    }
}
